import React, { useState } from 'react';
import { Search, Mail, FileText, CheckCircle, XCircle, Clock } from 'lucide-react';
import InsyncGuardInfo from '../../components/InsyncGuardInfo';

export default function AuditTrail() {
    const [query, setQuery] = useState('');
    const [searchType, setSearchType] = useState('email');
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!query.trim()) return;

        setLoading(true);
        setResults(null);

        try {
            const baseAudit = process.env.REACT_APP_AUDIT_ENDPOINT;
            const apiKey = process.env.REACT_APP_X_API_KEY;
            const params = new URLSearchParams();

            if (searchType === 'email') params.append('email_address', query.trim());
            else params.append('policy_ref', query.trim());

            const headers = { 'Content-Type': 'application/json' };
            if (apiKey) {
                headers['X-API-Key'] = apiKey;
            }
            const res = await fetch(`${baseAudit}?${params.toString()}`, { method: 'GET', headers });

            if (!res.ok) {
                throw new Error(`API error: ${res.status}`);
            }

            const body = await res.json();

            const events = (body && body.data && body.data.events) ? body.data.events : [];

            const mapped = events.map(ev => ({
                event_type: ev.event,
                timestamp: ev.timestamp,
                subject: ev.sg_template_name || ev.scheme_name || 'Email Event',
                recipient: searchType === 'email' ? query.trim() : (ev.policy_ref || ev.quote_ref || '—'),
                policy_ref: ev.policy_ref,
                quote_ref: ev.quote_ref,
                reason: ev.details ? ev.details.reason : null
            }));

            setResults(mapped);
        } catch (err) {
            console.error('Audit fetch failed', err);
            setResults([]);
        } finally {
            setLoading(false);
        }
    };

    const getEventIcon = (event_type) => {
        switch(event_type) {
            case 'delivered': return <CheckCircle className="h-5 w-5 text-green-500" />;
            case 'bounce':
            case 'dropped': return <XCircle className="h-5 w-5 text-red-500" />;
            case 'open':
            case 'click': return <Mail className="h-5 w-5 text-blue-500" />;
            default: return <Clock className="h-5 w-5 text-gray-400" />;
        }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-8">
            <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Communications Audit Trail</h1>
                <p className="text-gray-600">Trace Insync Email Service (IES) delivery history by recipient address or policy reference.</p>
                <InsyncGuardInfo className="mt-1 text-xs text-gray-500" />
            </div>

            <form onSubmit={handleSearch} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex flex-col md:flex-row gap-4 items-end">
                    <div className="flex-1 w-full">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Search Value</label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Search className="h-4 w-4 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                                placeholder={searchType === 'email' ? "e.g. client@example.co.uk" : "e.g. 00123456"}
                            />
                        </div>
                    </div>
                    
                    <div className="flex gap-4 items-center h-10">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input 
                                type="radio" 
                                name="searchType" 
                                value="email" 
                                checked={searchType === 'email'} 
                                onChange={() => setSearchType('email')}
                                className="text-orange-600 focus:ring-orange-500"
                            />
                            <span className="text-sm font-medium text-gray-700 flex items-center gap-1"><Mail className="h-4 w-4"/> Email</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input 
                                type="radio" 
                                name="searchType" 
                                value="policy" 
                                checked={searchType === 'policy'} 
                                onChange={() => setSearchType('policy')}
                                className="text-orange-600 focus:ring-orange-500"
                            />
                            <span className="text-sm font-medium text-gray-700 flex items-center gap-1"><FileText className="h-4 w-4"/> Policy Ref</span>
                        </label>
                    </div>

                    <button 
                        type="submit" 
                        disabled={loading || !query}
                        className="w-full md:w-auto px-6 py-2 bg-orange-600 text-white rounded-md font-medium hover:bg-orange-700 disabled:opacity-50 transition-colors"
                    >
                        {loading ? 'Searching...' : 'Search'}
                    </button>
                </div>
            </form>

            {results !== null && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6">Timeline Results</h3>
                    
                    {results.length === 0 ? (
                        <p className="text-gray-500 text-center py-8">No communication events found for this query.</p>
                    ) : (
                        <div className="relative border-l border-gray-200 ml-3 space-y-8">
                            {results.map((event, idx) => (
                                <div key={idx} className="relative pl-8">
                                    <div className="absolute -left-2.5 top-1 bg-white">
                                        {getEventIcon(event.event_type)}
                                    </div>
                                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-baseline mb-1">
                                        <h4 className="text-sm font-semibold text-gray-900 capitalize">
                                            {event.event_type}
                                        </h4>
                                        <time className="text-xs text-gray-500">
                                            {new Date(event.timestamp).toLocaleString('en-GB')}
                                        </time>
                                    </div>
                                    <div className="text-sm text-gray-600">
                                        <span className="font-medium text-gray-900">{event.subject}</span>
                                        <div className="mt-1">
                                            <span className="text-gray-500">To: </span>{event.recipient}
                                        </div>
                                        {event.reason && (
                                            <div className="mt-2 p-2 bg-red-50 border border-red-100 rounded text-red-700 text-xs font-mono">
                                                {event.reason}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}